import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { X, Filter, Search, MapPin, DollarSign, Users, Tag, Wrench, Package, Clock, Shield, Truck, Briefcase, Building } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

export interface FilterState {
  priceRange: [number, number] | null;
  seatingRange: [number, number] | null;
  tsnRange: [number, number] | null;
  tsoRange: [number, number] | null;
  csnRange: [number, number] | null;
  csoRange: [number, number] | null;
  manufacturers: string[];
  models: string[];
  variants?: string[];
  listingType: string;
  condition: string;
  yearRange: [number, number] | null;
  flightHoursMin?: number;
  flightHoursMax?: number;
  tsnMin?: number;
  tsnMax?: number;
  tsoMin?: number;
  tsoMax?: number;
  csnMin?: number;
  csnMax?: number;
  csoMin?: number;
  csoMax?: number;
  itemTypes?: string[];
  specificityTypes?: string[];
  shippingOptions?: string[];
  calibrationStatuses?: string[];
  certifications?: string[];
  location: string;
  // Jobs-specific filters
  jobCategories?: string[];
  employmentTypes?: string[];
  experienceMin?: number;
  experienceMax?: number;
  remoteOptions?: string[];
  salaryMin?: number;
  salaryMax?: number;
  // Services-specific filters
  serviceCategories?: string[];
  aogSupportOnly?: boolean;
  responseTimeMax?: string;
  regulatoryApprovals?: string[];
  pricingModels?: string[];
}

interface DatabaseData {
  manufacturers: string[];
  models: string[];
  variants: string[];
  conditions: string[];
  certifications: string[];
}

interface AdvancedFiltersProps {
  assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  className?: string;
}

const DatabaseDrivenFilters: React.FC<AdvancedFiltersProps> = ({
  assetType,
  filters,
  onFiltersChange,
  className = ""
}) => {
  const [databaseData, setDatabaseData] = useState<DatabaseData>({
    manufacturers: [],
    models: [],
    variants: [],
    conditions: [],
    certifications: [],
  });
  const [filteredModels, setFilteredModels] = useState<string[]>([]);
  const [itemTypes, setItemTypes] = useState<string[]>([]);
  const [specificityTypes, setSpecificityTypes] = useState<string[]>([]);
  const [shippingOptions, setShippingOptions] = useState<string[]>([]);
  const [calibrationStatuses, setCalibrationStatuses] = useState<string[]>([]);

  // Fetch database data on mount
  useEffect(() => {
    fetchDatabaseData();
    if (assetType === 'parts') {
      fetchPartsSpecificData();
    }
  }, [assetType]);

  useEffect(() => {
    // Filter models based on selected manufacturers
    if (filters.manufacturers.length > 0) {
      fetchModelsForManufacturers(filters.manufacturers);
    } else {
      setFilteredModels(databaseData.models);
    }
  }, [filters.manufacturers, databaseData.models]);

  const fetchDatabaseData = async () => {
    try {
      if (assetType === 'helicopter') {
        const [manufacturersResult, modelsResult, conditionsResult] = await Promise.all([
          supabase.from('helicopters').select('manufacturer').order('manufacturer'),
          supabase.from('helicopters').select('model').order('model'),
          supabase.from('helicopter_listings').select('condition').not('condition', 'is', null)
        ]);

        if (manufacturersResult.data) {
          const unique = Array.from(new Set(manufacturersResult.data.map(d => d.manufacturer).filter(Boolean))) as string[];
          setDatabaseData(prev => ({ ...prev, manufacturers: unique }));
        }
        if (modelsResult.data) {
          const unique = Array.from(new Set(modelsResult.data.map(d => d.model).filter(Boolean))) as string[];
          setDatabaseData(prev => ({ ...prev, models: unique }));
        }
        if (conditionsResult.data) {
          const unique = Array.from(new Set(conditionsResult.data.map(d => d.condition).filter(Boolean))) as string[];
          setDatabaseData(prev => ({ ...prev, conditions: unique }));
        }
      } else if (assetType === 'engine') {
        const [manufacturersResult, modelsResult, variantsResult, conditionsResult, certsResult] = await Promise.all([
          supabase.from('engine_listings').select('manufacturer').order('manufacturer'),
          supabase.from('engine_listings').select('model').order('model'),
          supabase.from('engine_listings').select('variant').not('variant', 'is', null).order('variant'),
          supabase.from('engine_listings').select('overall_condition').not('overall_condition', 'is', null),
          supabase.from('engine_listings').select('certifications').not('certifications', 'is', null)
        ]);

        if (manufacturersResult.data) {
          const unique = Array.from(new Set(manufacturersResult.data.map(d => d.manufacturer).filter(Boolean))) as string[];
          setDatabaseData(prev => ({ ...prev, manufacturers: unique }));
        }
        if (modelsResult.data) {
          const unique = Array.from(new Set(modelsResult.data.map(d => d.model).filter(Boolean))) as string[];
          setDatabaseData(prev => ({ ...prev, models: unique }));
        }
        if (variantsResult.data) {
          const unique = Array.from(new Set(variantsResult.data.map(d => d.variant).filter(Boolean))) as string[];
          setDatabaseData(prev => ({ ...prev, variants: unique }));
        }
        if (conditionsResult.data) {
          const unique = Array.from(new Set(conditionsResult.data.map(d => d.overall_condition).filter(Boolean))) as string[];
          setDatabaseData(prev => ({ ...prev, conditions: unique }));
        }
        if (certsResult.data) {
          const allCerts = certsResult.data.flatMap(d => d.certifications || []);
          const unique = Array.from(new Set(allCerts.filter(Boolean))) as string[];
          setDatabaseData(prev => ({ ...prev, certifications: unique }));
        }
      } else if (assetType === 'parts') {
        const conditionsResult = await supabase.from('parts_listings').select('condition').not('condition', 'is', null);
        if (conditionsResult.data) {
          const unique = Array.from(new Set(conditionsResult.data.map(d => d.condition).filter(Boolean))) as string[];
          setDatabaseData(prev => ({ ...prev, conditions: unique }));
        }
      }
    } catch (error) {
      console.error('Error fetching database data:', error);
    }
  };

  const fetchPartsSpecificData = async () => {
    try {
      const { data: partsData } = await supabase
        .from('parts_listings')
        .select('item_type, specificity_type, shipping_options, calibration_status')
        .eq('status', 'active');

      if (partsData) {
        const uniqueItemTypes = Array.from(new Set(partsData.map(d => d.item_type).filter(Boolean)));
        const uniqueSpecificityTypes = Array.from(new Set(partsData.map(d => d.specificity_type).filter(Boolean)));
        const allShippingOptions = partsData.flatMap(d => d.shipping_options || []);
        const uniqueShipping = Array.from(new Set(allShippingOptions));
        const uniqueCalibration = Array.from(new Set(partsData.map(d => d.calibration_status).filter(Boolean)));

        setItemTypes(uniqueItemTypes);
        setSpecificityTypes(uniqueSpecificityTypes);
        setShippingOptions(uniqueShipping);
        setCalibrationStatuses(uniqueCalibration);
      }
    } catch (error) {
      console.error('Error fetching parts-specific data:', error);
    }
  };

  const fetchModelsForManufacturers = async (selectedManufacturers: string[]) => {
    try {
      const table = assetType === 'helicopter' ? 'helicopters' : 'engine_listings';
      const { data } = await supabase
        .from(table)
        .select('model')
        .in('manufacturer', selectedManufacturers)
        .order('model');
      
      const models = [...new Set(data?.map(item => item.model) || [])];
      setFilteredModels(models as string[]);
    } catch (error) {
      console.error('Error fetching models for manufacturers:', error);
    }
  };

  const updateFilters = (updates: Partial<FilterState>) => {
    onFiltersChange({ ...filters, ...updates });
  };

  const clearFilters = () => {
    onFiltersChange({
      priceRange: null,
      seatingRange: null,
      tsnRange: null,
      tsoRange: null,
      csnRange: null,
      csoRange: null,
      manufacturers: [],
      models: [],
      variants: [],
      listingType: 'all',
      condition: 'all',
      yearRange: null,
      flightHoursMin: undefined,
      flightHoursMax: undefined,
      tsnMin: undefined,
      tsnMax: undefined,
      tsoMin: undefined,
      tsoMax: undefined,
      csnMin: undefined,
      csnMax: undefined,
      csoMin: undefined,
      csoMax: undefined,
      itemTypes: [],
      specificityTypes: [],
      shippingOptions: [],
      calibrationStatuses: [],
      certifications: [],
      location: '',
      // Jobs-specific
      jobCategories: [],
      employmentTypes: [],
      experienceMin: undefined,
      experienceMax: undefined,
      remoteOptions: [],
      salaryMin: undefined,
      salaryMax: undefined,
    });
  };

  const toggleManufacturer = (manufacturer: string) => {
    const current = filters.manufacturers;
    const updated = current.includes(manufacturer)
      ? current.filter(m => m !== manufacturer)
      : [...current, manufacturer];
    updateFilters({ 
      manufacturers: updated,
      models: [] // Reset models when manufacturers change
    });
  };

  const toggleModel = (model: string) => {
    const current = filters.models;
    const updated = current.includes(model)
      ? current.filter(m => m !== model)
      : [...current, model];
    updateFilters({ models: updated });
  };

  const toggleVariant = (variant: string) => {
    const current = filters.variants || [];
    const updated = current.includes(variant)
      ? current.filter(v => v !== variant)
      : [...current, variant];
    updateFilters({ variants: updated });
  };

  const toggleCertification = (cert: string) => {
    const current = filters.certifications || [];
    const updated = current.includes(cert)
      ? current.filter(c => c !== cert)
      : [...current, cert];
    updateFilters({ certifications: updated });
  };

  const activeFiltersCount = [
    filters.manufacturers.length > 0,
    filters.models.length > 0,
    (filters.variants?.length || 0) > 0,
    filters.condition !== 'all',
    filters.location?.trim() !== '',
    filters.priceRange !== null,
    (filters.certifications?.length || 0) > 0,
    assetType === 'helicopter' && filters.seatingRange !== null,
    assetType === 'helicopter' && filters.yearRange !== null,
    assetType === 'helicopter' && (filters.flightHoursMin || filters.flightHoursMax),
    assetType === 'engine' && (filters.tsnMin || filters.tsnMax),
    assetType === 'engine' && (filters.tsoMin || filters.tsoMax),
    assetType === 'engine' && (filters.csnMin || filters.csnMax),
    assetType === 'engine' && (filters.csoMin || filters.csoMax),
    assetType === 'parts' && (filters.itemTypes?.length || 0) > 0,
    assetType === 'parts' && (filters.specificityTypes?.length || 0) > 0,
    assetType === 'parts' && (filters.shippingOptions?.length || 0) > 0,
    assetType === 'parts' && (filters.calibrationStatuses?.length || 0) > 0,
    // Jobs-specific filters
    assetType === 'jobs' && (filters.jobCategories?.length || 0) > 0,
    assetType === 'jobs' && (filters.employmentTypes?.length || 0) > 0,
    assetType === 'jobs' && (filters.remoteOptions?.length || 0) > 0,
    assetType === 'jobs' && (filters.experienceMin || filters.experienceMax),
    assetType === 'jobs' && (filters.salaryMin || filters.salaryMax),
  ].filter(Boolean).length;

  // Section Header Component
  const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
    <div className="flex items-center gap-2 pb-2">
      <div className="p-1.5 rounded-lg bg-primary/10">
        <Icon className="h-3.5 w-3.5 text-primary" />
      </div>
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</span>
    </div>
  );

  // Badge Filter Component
  const BadgeFilter = ({ 
    items, 
    selected, 
    onToggle,
    maxHeight = "max-h-32"
  }: { 
    items: string[]; 
    selected: string[]; 
    onToggle: (item: string) => void;
    maxHeight?: string;
  }) => (
    <div className={`flex flex-wrap gap-1.5 ${maxHeight} overflow-y-auto`}>
      {items.map((item) => (
        <Badge
          key={item}
          variant={selected.includes(item) ? "default" : "outline"}
          className="cursor-pointer hover:scale-105 transition-transform text-[11px] py-0.5 px-2"
          onClick={() => onToggle(item)}
        >
          {item}
          {selected.includes(item) && <X className="h-2.5 w-2.5 ml-1" />}
        </Badge>
      ))}
    </div>
  );

  // Min/Max Input Component
  const MinMaxInput = ({ 
    label, 
    minValue, 
    maxValue, 
    onMinChange, 
    onMaxChange,
    placeholder = { min: "Min", max: "Max" }
  }: {
    label: string;
    minValue?: number;
    maxValue?: number;
    onMinChange: (val: number | undefined) => void;
    onMaxChange: (val: number | undefined) => void;
    placeholder?: { min: string; max: string };
  }) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <div className="grid grid-cols-2 gap-2">
        <Input
          type="number"
          placeholder={placeholder.min}
          value={minValue || ''}
          onChange={(e) => onMinChange(parseInt(e.target.value) || undefined)}
          min={0}
          className="h-8 text-xs"
        />
        <Input
          type="number"
          placeholder={placeholder.max}
          value={maxValue || ''}
          onChange={(e) => onMaxChange(parseInt(e.target.value) || undefined)}
          min={0}
          className="h-8 text-xs"
        />
      </div>
    </div>
  );

  return (
    <Card className={`${className} glass-card border-0 shadow-luxury`}>
      <CardHeader className="pb-3 px-3 md:px-6">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-gradient-luxury">
              <Filter className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="text-sm md:text-base font-bold">Advanced Filters</div>
            </div>
            {activeFiltersCount > 0 && (
              <Badge variant="secondary" className="ml-1 bg-gradient-luxury text-white text-[10px] h-5 px-1.5">
                {activeFiltersCount}
              </Badge>
            )}
          </CardTitle>
          {activeFiltersCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs h-7 px-2">
              Clear All
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4 px-3 md:px-6 pb-4">
        
        {/* ===== SECTION 1: IDENTITY FILTERS ===== */}
        <div className="space-y-3">
          <SectionHeader icon={assetType === 'jobs' ? Briefcase : Tag} title={assetType === 'jobs' ? 'Job Details' : 'Identity'} />
          
          {/* Manufacturers - Helicopter & Engine */}
          {(assetType === 'helicopter' || assetType === 'engine') && databaseData.manufacturers.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Manufacturer</Label>
                {filters.manufacturers.length > 0 && (
                  <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                    {filters.manufacturers.length}
                  </Badge>
                )}
              </div>
              <BadgeFilter
                items={databaseData.manufacturers}
                selected={filters.manufacturers}
                onToggle={toggleManufacturer}
                maxHeight="max-h-24"
              />
            </div>
          )}

          {/* Models - Dynamic based on manufacturers */}
          {(assetType === 'helicopter' || assetType === 'engine') && filters.manufacturers.length > 0 && filteredModels.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Model</Label>
                {filters.models.length > 0 && (
                  <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                    {filters.models.length}
                  </Badge>
                )}
              </div>
              <BadgeFilter
                items={filteredModels}
                selected={filters.models}
                onToggle={toggleModel}
                maxHeight="max-h-20"
              />
            </div>
          )}

          {/* Variants - Engine only */}
          {assetType === 'engine' && databaseData.variants.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Variant</Label>
                {(filters.variants?.length || 0) > 0 && (
                  <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                    {filters.variants?.length}
                  </Badge>
                )}
              </div>
              <BadgeFilter
                items={databaseData.variants}
                selected={filters.variants || []}
                onToggle={toggleVariant}
                maxHeight="max-h-20"
              />
            </div>
          )}

          {/* Item Type - Parts only */}
          {assetType === 'parts' && itemTypes.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-medium">Item Type</Label>
              <BadgeFilter
                items={itemTypes}
                selected={filters.itemTypes || []}
                onToggle={(type) => {
                  const current = filters.itemTypes || [];
                  const updated = current.includes(type) ? current.filter(t => t !== type) : [...current, type];
                  updateFilters({ itemTypes: updated });
                }}
              />
            </div>
          )}

          {/* Specificity - Parts only */}
          {assetType === 'parts' && specificityTypes.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-medium">Specificity</Label>
              <BadgeFilter
                items={specificityTypes}
                selected={filters.specificityTypes || []}
                onToggle={(type) => {
                  const current = filters.specificityTypes || [];
                  const updated = current.includes(type) ? current.filter(t => t !== type) : [...current, type];
                  updateFilters({ specificityTypes: updated });
                }}
              />
            </div>
          )}

          {/* ===== JOBS-SPECIFIC FILTERS ===== */}
          {assetType === 'jobs' && (
            <>
              {/* Job Category - Matches wizard categories exactly */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Job Category</Label>
                  {(filters.jobCategories?.length || 0) > 0 && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                      {filters.jobCategories?.length}
                    </Badge>
                  )}
                </div>
                <BadgeFilter
                  items={[
                    'Pilots',
                    'Mechanics & Maintenance',
                    'CAMO / Continuing Airworthiness',
                    'Part-21 (Design & Production)',
                    'Cabin Crew',
                    'Ground Operations',
                    'ATC & Dispatch',
                    'Training & Education',
                    'Drones & UAS',
                    'Airport Operations',
                    'Engineering & Technical',
                    'Management & Administration'
                  ]}
                  selected={(filters.jobCategories || []).map(cat => {
                    const labelMap: Record<string, string> = {
                      'pilot': 'Pilots',
                      'mechanic': 'Mechanics & Maintenance',
                      'camo': 'CAMO / Continuing Airworthiness',
                      'part21': 'Part-21 (Design & Production)',
                      'cabin_crew': 'Cabin Crew',
                      'ground_ops': 'Ground Operations',
                      'atc_dispatch': 'ATC & Dispatch',
                      'training': 'Training & Education',
                      'drones_uas': 'Drones & UAS',
                      'airport_ops': 'Airport Operations',
                      'engineering': 'Engineering & Technical',
                      'management': 'Management & Administration',
                    };
                    return labelMap[cat] || cat;
                  })}
                  onToggle={(label) => {
                    const valueMap: Record<string, string> = {
                      'Pilots': 'pilot',
                      'Mechanics & Maintenance': 'mechanic',
                      'CAMO / Continuing Airworthiness': 'camo',
                      'Part-21 (Design & Production)': 'part21',
                      'Cabin Crew': 'cabin_crew',
                      'Ground Operations': 'ground_ops',
                      'ATC & Dispatch': 'atc_dispatch',
                      'Training & Education': 'training',
                      'Drones & UAS': 'drones_uas',
                      'Airport Operations': 'airport_ops',
                      'Engineering & Technical': 'engineering',
                      'Management & Administration': 'management',
                    };
                    const value = valueMap[label] || label.toLowerCase().replace(/\s+/g, '_');
                    const current = filters.jobCategories || [];
                    const updated = current.includes(value) ? current.filter(c => c !== value) : [...current, value];
                    updateFilters({ jobCategories: updated });
                  }}
                  maxHeight="max-h-32"
                />
              </div>

              {/* Employment Type - Matches wizard types exactly */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Employment Type</Label>
                  {(filters.employmentTypes?.length || 0) > 0 && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                      {filters.employmentTypes?.length}
                    </Badge>
                  )}
                </div>
                <BadgeFilter
                  items={['Full-Time', 'Part-Time', 'Contract', 'Freelance', 'Internship', 'Seasonal']}
                  selected={(filters.employmentTypes || []).map(type => {
                    const labelMap: Record<string, string> = {
                      'full_time': 'Full-Time',
                      'part_time': 'Part-Time',
                      'contract': 'Contract',
                      'freelance': 'Freelance',
                      'internship': 'Internship',
                      'seasonal': 'Seasonal',
                    };
                    return labelMap[type] || type;
                  })}
                  onToggle={(label) => {
                    const valueMap: Record<string, string> = {
                      'Full-Time': 'full_time',
                      'Part-Time': 'part_time',
                      'Contract': 'contract',
                      'Freelance': 'freelance',
                      'Internship': 'internship',
                      'Seasonal': 'seasonal',
                    };
                    const value = valueMap[label] || label.toLowerCase().replace('-', '_');
                    const current = filters.employmentTypes || [];
                    const updated = current.includes(value) ? current.filter(t => t !== value) : [...current, value];
                    updateFilters({ employmentTypes: updated });
                  }}
                />
              </div>

              {/* Remote Options */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Work Location</Label>
                  {(filters.remoteOptions?.length || 0) > 0 && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                      {filters.remoteOptions?.length}
                    </Badge>
                  )}
                </div>
                <BadgeFilter
                  items={['Onsite', 'Remote', 'Hybrid']}
                  selected={(filters.remoteOptions || []).map(opt => {
                    const labelMap: Record<string, string> = {
                      'onsite': 'Onsite',
                      'remote': 'Remote',
                      'hybrid': 'Hybrid',
                    };
                    return labelMap[opt] || opt;
                  })}
                  onToggle={(label) => {
                    const value = label.toLowerCase();
                    const current = filters.remoteOptions || [];
                    const updated = current.includes(value) ? current.filter(o => o !== value) : [...current, value];
                    updateFilters({ remoteOptions: updated });
                  }}
                />
              </div>

              {/* Experience Range */}
              <MinMaxInput
                label="Experience (Years)"
                minValue={filters.experienceMin}
                maxValue={filters.experienceMax}
                onMinChange={(val) => updateFilters({ experienceMin: val })}
                onMaxChange={(val) => updateFilters({ experienceMax: val })}
                placeholder={{ min: "Min years", max: "Max years" }}
              />

              {/* Salary Range */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Salary Range (USD/year)</Label>
                  {(filters.salaryMin || filters.salaryMax) && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-5 text-[10px] px-1.5" 
                      onClick={() => updateFilters({ salaryMin: undefined, salaryMax: undefined })}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="number"
                    placeholder="Min $"
                    value={filters.salaryMin || ''}
                    onChange={(e) => updateFilters({ salaryMin: parseInt(e.target.value) || undefined })}
                    min={0}
                    className="h-8 text-xs"
                  />
                  <Input
                    type="number"
                    placeholder="Max $"
                    value={filters.salaryMax || ''}
                    onChange={(e) => updateFilters({ salaryMax: parseInt(e.target.value) || undefined })}
                    min={0}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            </>
          )}

          {/* ===== SERVICES-SPECIFIC FILTERS ===== */}
          {assetType === 'services' && (
            <>
              {/* Service Category */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Service Category</Label>
                  {(filters.serviceCategories?.length || 0) > 0 && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                      {filters.serviceCategories?.length}
                    </Badge>
                  )}
                </div>
                <BadgeFilter
                  items={[
                    'MRO / Part-145',
                    'CAMO',
                    'Part-21 DOA/POA',
                    'Training & Education',
                    'Operator Services',
                    'Engineering',
                    'Ground Operations',
                    'FBO / Heliport',
                    'Management',
                    'Drone / UAS'
                  ]}
                  selected={(filters.serviceCategories || []).map(cat => {
                    const labelMap: Record<string, string> = {
                      'mro': 'MRO / Part-145',
                      'camo': 'CAMO',
                      'part21': 'Part-21 DOA/POA',
                      'training': 'Training & Education',
                      'operator': 'Operator Services',
                      'engineering': 'Engineering',
                      'ground_ops': 'Ground Operations',
                      'fbo_heliport': 'FBO / Heliport',
                      'management': 'Management',
                      'uas': 'Drone / UAS',
                    };
                    return labelMap[cat] || cat;
                  })}
                  onToggle={(label) => {
                    const valueMap: Record<string, string> = {
                      'MRO / Part-145': 'mro',
                      'CAMO': 'camo',
                      'Part-21 DOA/POA': 'part21',
                      'Training & Education': 'training',
                      'Operator Services': 'operator',
                      'Engineering': 'engineering',
                      'Ground Operations': 'ground_ops',
                      'FBO / Heliport': 'fbo_heliport',
                      'Management': 'management',
                      'Drone / UAS': 'uas',
                    };
                    const value = valueMap[label] || label.toLowerCase().replace(/[\s\/]+/g, '_');
                    const current = filters.serviceCategories || [];
                    const updated = current.includes(value) ? current.filter(c => c !== value) : [...current, value];
                    updateFilters({ serviceCategories: updated });
                  }}
                  maxHeight="max-h-32"
                />
              </div>

              {/* AOG Support Toggle */}
              <div className="flex items-center justify-between p-2.5 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/10 rounded-lg border border-red-200/50 dark:border-red-700/30">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-red-600 dark:text-red-400" />
                  <div>
                    <Label className="text-xs font-medium">AOG Support Only</Label>
                    <p className="text-[10px] text-muted-foreground">24/7 Aircraft on Ground support</p>
                  </div>
                </div>
                <Button
                  variant={filters.aogSupportOnly ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    "h-7 text-xs",
                    filters.aogSupportOnly && "bg-red-600 hover:bg-red-700"
                  )}
                  onClick={() => updateFilters({ aogSupportOnly: !filters.aogSupportOnly })}
                >
                  {filters.aogSupportOnly ? '✓ On' : 'Off'}
                </Button>
              </div>

              {/* Response Time */}
              <div className="space-y-2">
                <Label className="text-xs font-medium">Response Time</Label>
                <Select
                  value={filters.responseTimeMax || '__any__'}
                  onValueChange={(val) => updateFilters({ responseTimeMax: val === '__any__' ? undefined : val })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Any response time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">Any response time</SelectItem>
                    <SelectItem value="2hrs">Within 2 hours</SelectItem>
                    <SelectItem value="4hrs">Within 4 hours</SelectItem>
                    <SelectItem value="same_day">Same day</SelectItem>
                    <SelectItem value="next_day">Next day</SelectItem>
                    <SelectItem value="48hrs">Within 48 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Regulatory Approvals */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Regulatory Approvals</Label>
                  {(filters.regulatoryApprovals?.length || 0) > 0 && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                      {filters.regulatoryApprovals?.length}
                    </Badge>
                  )}
                </div>
                <BadgeFilter
                  items={[
                    'EASA Part-145',
                    'FAA Part-145',
                    'EASA Part-CAMO',
                    'EASA Part-21 DOA',
                    'EASA Part-21 POA',
                    'FAA Repair Station',
                    'Transport Canada',
                    'ANAC Brazil',
                    'UK CAA',
                    'GCAA UAE'
                  ]}
                  selected={filters.regulatoryApprovals || []}
                  onToggle={(approval) => {
                    const current = filters.regulatoryApprovals || [];
                    const updated = current.includes(approval) ? current.filter(a => a !== approval) : [...current, approval];
                    updateFilters({ regulatoryApprovals: updated });
                  }}
                  maxHeight="max-h-28"
                />
              </div>

              {/* Pricing Model */}
              <div className="space-y-2">
                <Label className="text-xs font-medium">Pricing Model</Label>
                <BadgeFilter
                  items={['Fixed Rate', 'Hourly', 'Quote-based', 'Contract']}
                  selected={(filters.pricingModels || []).map(p => {
                    const labelMap: Record<string, string> = {
                      'fixed': 'Fixed Rate',
                      'hourly': 'Hourly',
                      'quote': 'Quote-based',
                      'contract': 'Contract',
                    };
                    return labelMap[p] || p;
                  })}
                  onToggle={(label) => {
                    const valueMap: Record<string, string> = {
                      'Fixed Rate': 'fixed',
                      'Hourly': 'hourly',
                      'Quote-based': 'quote',
                      'Contract': 'contract',
                    };
                    const value = valueMap[label] || label.toLowerCase();
                    const current = filters.pricingModels || [];
                    const updated = current.includes(value) ? current.filter(p => p !== value) : [...current, value];
                    updateFilters({ pricingModels: updated });
                  }}
                />
              </div>
            </>
          )}
        </div>

        <Separator className="opacity-40" />

        {/* ===== SECTION 2: CONDITION & SPECS ===== */}
        <div className="space-y-3">
          <SectionHeader icon={Wrench} title="Condition & Specs" />

          {/* Condition Dropdown */}
          {databaseData.conditions.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Condition</Label>
              <Select value={filters.condition} onValueChange={(value) => updateFilters({ condition: value })}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="All Conditions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Conditions</SelectItem>
                  {databaseData.conditions.map(condition => (
                    <SelectItem key={condition} value={condition.toLowerCase()}>
                      {condition}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Certifications - Engine only */}
          {assetType === 'engine' && databaseData.certifications.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Shield className="h-3 w-3 text-muted-foreground" />
                <Label className="text-xs font-medium">Certifications</Label>
              </div>
              <BadgeFilter
                items={databaseData.certifications}
                selected={filters.certifications || []}
                onToggle={toggleCertification}
              />
            </div>
          )}

          {/* Helicopter: Year & Seating */}
          {assetType === 'helicopter' && (
            <>
              <MinMaxInput
                label="Manufacturing Year"
                minValue={filters.yearRange?.[0]}
                maxValue={filters.yearRange?.[1]}
                onMinChange={(val) => updateFilters({ yearRange: [val || 1950, filters.yearRange?.[1] || new Date().getFullYear()] })}
                onMaxChange={(val) => updateFilters({ yearRange: [filters.yearRange?.[0] || 1950, val || new Date().getFullYear()] })}
                placeholder={{ min: "From year", max: "To year" }}
              />
              <MinMaxInput
                label="Flight Hours (TTSN)"
                minValue={filters.flightHoursMin}
                maxValue={filters.flightHoursMax}
                onMinChange={(val) => updateFilters({ flightHoursMin: val })}
                onMaxChange={(val) => updateFilters({ flightHoursMax: val })}
              />
              <div className="space-y-1.5">
                <Label className="text-xs font-medium flex items-center gap-1.5">
                  <Users className="h-3 w-3 text-muted-foreground" />
                  Seating Capacity
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="number"
                    placeholder="Min seats"
                    value={filters.seatingRange?.[0] || ''}
                    onChange={(e) => updateFilters({ seatingRange: [parseInt(e.target.value) || 1, filters.seatingRange?.[1] || 20] })}
                    min={1}
                    max={20}
                    className="h-8 text-xs"
                  />
                  <Input
                    type="number"
                    placeholder="Max seats"
                    value={filters.seatingRange?.[1] || ''}
                    onChange={(e) => updateFilters({ seatingRange: [filters.seatingRange?.[0] || 1, parseInt(e.target.value) || 20] })}
                    min={1}
                    max={20}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            </>
          )}

          {/* Engine: TSN/TSO/CSN/CSO */}
          {assetType === 'engine' && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              <MinMaxInput
                label="TSN"
                minValue={filters.tsnMin}
                maxValue={filters.tsnMax}
                onMinChange={(val) => updateFilters({ tsnMin: val })}
                onMaxChange={(val) => updateFilters({ tsnMax: val })}
              />
              <MinMaxInput
                label="TSO"
                minValue={filters.tsoMin}
                maxValue={filters.tsoMax}
                onMinChange={(val) => updateFilters({ tsoMin: val })}
                onMaxChange={(val) => updateFilters({ tsoMax: val })}
              />
              <MinMaxInput
                label="CSN"
                minValue={filters.csnMin}
                maxValue={filters.csnMax}
                onMinChange={(val) => updateFilters({ csnMin: val })}
                onMaxChange={(val) => updateFilters({ csnMax: val })}
              />
              <MinMaxInput
                label="CSO"
                minValue={filters.csoMin}
                maxValue={filters.csoMax}
                onMinChange={(val) => updateFilters({ csoMin: val })}
                onMaxChange={(val) => updateFilters({ csoMax: val })}
              />
            </div>
          )}

          {/* Parts: Calibration & Shipping */}
          {assetType === 'parts' && (
            <>
              {calibrationStatuses.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <Label className="text-xs font-medium">Calibration Status</Label>
                  </div>
                  <BadgeFilter
                    items={calibrationStatuses}
                    selected={filters.calibrationStatuses || []}
                    onToggle={(status) => {
                      const current = filters.calibrationStatuses || [];
                      const updated = current.includes(status) ? current.filter(s => s !== status) : [...current, status];
                      updateFilters({ calibrationStatuses: updated });
                    }}
                  />
                </div>
              )}
              {shippingOptions.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Truck className="h-3 w-3 text-muted-foreground" />
                    <Label className="text-xs font-medium">Shipping Options</Label>
                  </div>
                  <BadgeFilter
                    items={shippingOptions}
                    selected={filters.shippingOptions || []}
                    onToggle={(option) => {
                      const current = filters.shippingOptions || [];
                      const updated = current.includes(option) ? current.filter(o => o !== option) : [...current, option];
                      updateFilters({ shippingOptions: updated });
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <Separator className="opacity-40" />

        {/* ===== SECTION 3: PRICE & LOCATION ===== */}
        <div className="space-y-3">
          <SectionHeader icon={DollarSign} title="Price & Location" />

          {/* Price Range */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Price Range (USD)</Label>
              {filters.priceRange && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-5 text-[10px] px-1.5" 
                  onClick={() => updateFilters({ priceRange: null })}
                >
                  Clear
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="number"
                placeholder="Min $"
                value={filters.priceRange?.[0] || ''}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  updateFilters({ priceRange: [val, filters.priceRange?.[1] || 50000000] });
                }}
                min={0}
                className="h-8 text-xs"
              />
              <Input
                type="number"
                placeholder="Max $"
                value={filters.priceRange?.[1] || ''}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 50000000;
                  updateFilters({ priceRange: [filters.priceRange?.[0] || 0, val] });
                }}
                min={0}
                className="h-8 text-xs"
              />
            </div>
            {filters.priceRange && (
              <div className="flex justify-between text-[10px] text-muted-foreground px-1">
                <span>${filters.priceRange[0].toLocaleString()}</span>
                <span>${filters.priceRange[1].toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Location */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium flex items-center gap-1.5">
              <MapPin className="h-3 w-3 text-muted-foreground" />
              Location
            </Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="City, state, or country..."
                value={filters.location}
                onChange={(e) => updateFilters({ location: e.target.value })}
                className="pl-8 h-8 text-xs"
              />
            </div>
          </div>
        </div>

      </CardContent>
    </Card>
  );
};

export default DatabaseDrivenFilters;
